import { all, call, put, takeLatest } from '@redux-saga/core/effects';
import axios, { AxiosResponse } from 'axios';

import { AttendanceSession } from '../../types/entity.types';
import { SagaPayload } from '../api.types';
import {
  CreateAccountabilityTemplateRequest,
  CreateAccountabilityTemplateResponse,
  GenerateAccountabilityTemplateSessionRequest,
  GenerateAccountabilityTemplateSessionResponse,
  UpdateAccountabilityTemplateRequest,
  UpdateAccountabilityTemplateResponse,
  ViewAccountabilityTemplateRequest,
  ViewAccountabilityTemplateResponse
} from './accountability-template.types';

import {
  CREATING_ACCOUNTABILITY_TEMPLATE,
  GENERATING_SESSIONS,
  RETRIEVING_ACCOUNTABILITY_TEMPLATE,
  UPDATING_ACCOUNTABILITY_TEMPLATE
} from './accountability-template.constants';

import {
  requestCreateAccountabilityTemplate,
  requestGenerateAccountabilityTemplateSessions,
  requestUpdateAccountabilityTemplate,
  requestViewAccountabilityTemplate
} from './accountability-template.actions';

import {
  getCreateAccountabilityTemplateRequest,
  getGenerateAccountabilityTemplateSessionRequest,
  getUpdateAccountabilityTemplateRequest,
  getViewAccountabilityTemplateRequest
} from './accountability-template.endpoints';

import { setAccountabilityTemplate } from '../../slices/programme/accountability/accountability.slice';
import { addSpinner, addSystemNotice, removeSpinner } from '../../slices/system/system.slice';

import { stripEmptyTemplates } from '../../utils/accountability.util';
import { getErrorMessage } from '../../utils/api.util';
import { ERROR, SUCCESS } from '../../utils/constants';

export function* performCreateAccountabilityTemplateRequest({
  payload
}: SagaPayload<CreateAccountabilityTemplateRequest>) {
  const { requestData, onSuccess } = payload;

  try {
    yield put(addSpinner(CREATING_ACCOUNTABILITY_TEMPLATE));

    const [endpoint, options] = getCreateAccountabilityTemplateRequest(stripEmptyTemplates(requestData));

    const { data }: AxiosResponse<CreateAccountabilityTemplateResponse> = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess, undefined);
    }

    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
    yield put(
      requestViewAccountabilityTemplate({
        requestData: { programmeExternalReference: requestData.accountabilityTemplate.programmeObjectReference! }
      })
    );
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(CREATING_ACCOUNTABILITY_TEMPLATE));
  }
}

export function* watchForCreateAccountabilityTemplateRequest() {
  yield takeLatest(requestCreateAccountabilityTemplate.type, performCreateAccountabilityTemplateRequest);
}

export function* performViewAccountabilityTemplateRequest({ payload }: SagaPayload<ViewAccountabilityTemplateRequest>) {
  const { requestData, onSuccess } = payload;

  try {
    yield put(addSpinner(RETRIEVING_ACCOUNTABILITY_TEMPLATE));

    const [endpoint, options] = getViewAccountabilityTemplateRequest(requestData);

    const { data }: AxiosResponse<ViewAccountabilityTemplateResponse> = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess, undefined);
    }

    yield put(setAccountabilityTemplate(data.accountabilityTemplates[0]));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(RETRIEVING_ACCOUNTABILITY_TEMPLATE));
  }
}

export function* watchForViewAccountabilityTemplateRequest() {
  yield takeLatest(requestViewAccountabilityTemplate.type, performViewAccountabilityTemplateRequest);
}

export function* performUpdateAccountabilityTemplateRequest({
  payload
}: SagaPayload<UpdateAccountabilityTemplateRequest>) {
  const { requestData, onSuccess } = payload;

  try {
    yield put(addSpinner(UPDATING_ACCOUNTABILITY_TEMPLATE));

    const [endpoint, options] = getUpdateAccountabilityTemplateRequest(stripEmptyTemplates(requestData));

    const { data }: AxiosResponse<UpdateAccountabilityTemplateResponse> = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess, undefined);
    }

    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(UPDATING_ACCOUNTABILITY_TEMPLATE));
  }
}

export function* watchForUpdateAccountabilityTemplateRequest() {
  yield takeLatest(requestUpdateAccountabilityTemplate.type, performUpdateAccountabilityTemplateRequest);
}

export function* performGenerateAccountabilityTemplateSessionsRequest({
  payload
}: SagaPayload<GenerateAccountabilityTemplateSessionRequest, AttendanceSession[]>) {
  const { requestData, onSuccess } = payload;

  try {
    yield put(addSpinner(GENERATING_SESSIONS));

    const [endpoint, options] = getGenerateAccountabilityTemplateSessionRequest(requestData);

    const { data }: AxiosResponse<GenerateAccountabilityTemplateSessionResponse> = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess, data.sessionTemplates);
    }

    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(GENERATING_SESSIONS));
  }
}

export function* watchForGenerateAccountabilityTemplateSessionsRequest() {
  yield takeLatest(
    requestGenerateAccountabilityTemplateSessions.type,
    performGenerateAccountabilityTemplateSessionsRequest
  );
}

export default function* accountabilityTemplateSaga() {
  yield all([
    watchForCreateAccountabilityTemplateRequest(),
    watchForViewAccountabilityTemplateRequest(),
    watchForUpdateAccountabilityTemplateRequest(),
    watchForGenerateAccountabilityTemplateSessionsRequest()
  ]);
}
